<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

// Botón junto a "Gestión Plus" que abre el registro de Zoho CRM (Lead o
// SDI/Deals) del contacto en una pestaña nueva. Aparece SIEMPRE que el
// contacto tenga teléfono o email y la integración esté disponible.
// La selección es AUTOMÁTICA: el registro más reciente cuyo estado no sea
// "Caída". Si ya hay un destino recordado (crm_note_target), lo abre directo.
const props = defineProps({
  conversation: { type: Object, required: true },
});

const { t } = useI18n();
const store = useStore();

const accountId = computed(() => store.getters.getCurrentAccountId);
const contactId = computed(() => props.conversation?.meta?.sender?.id || null);
const contact = computed(() =>
  contactId.value ? store.getters['contacts/getContact'](contactId.value) : null
);
const target = computed(
  () => contact.value?.custom_attributes?.crm_note_target || null
);
const phone = computed(() => contact.value?.phone_number || '');
const email = computed(() => contact.value?.email || '');

const hasAccess = computed(() => {
  const account = store.getters['accounts/getAccount'](accountId.value) || {};
  const settings = account.settings || {};
  const enabled =
    settings.crm_notes_sync_enabled === true ||
    settings.crm_notes_sync_enabled === 'true';
  if (!enabled) return false;
  if (store.getters.getCurrentRole === 'administrator') return true;
  const allowed = Array.isArray(settings.crm_notes_sync_roles)
    ? settings.crm_notes_sync_roles.map(r => String(r))
    : [];
  if (!allowed.length) return false;
  const userRoles = [
    String(store.getters.getCurrentRole || ''),
    String(store.getters.getCurrentCustomRoleId || ''),
  ];
  return userRoles.some(r => allowed.includes(r));
});

const isVisible = computed(
  () => hasAccess.value && (!!phone.value || !!email.value)
);

const crmBaseUrl = computed(() => {
  const apiDomain =
    store.getters['zohoCrmIntegration/getStatus']?.api_domain ||
    'https://www.zohoapis.com';
  const match = apiDomain.match(/zohoapis\.([a-z.]+)$/i);
  const tld = match ? match[1] : 'com';
  return `https://crm.zoho.${tld}`;
});

const recordUrl = ({ module: mod, record_id: recordId }) =>
  `${crmBaseUrl.value}/crm/tab/${mod}/${recordId}`;

const openUrl = url => {
  // eslint-disable-next-line no-console
  console.log('[open-in-crm] opening', url);
  window.open(url, '_blank', 'noopener');
};

const rememberTarget = record => {
  const contactValue = contact.value;
  const existing = contactValue.custom_attributes || {};
  store.dispatch('contacts/update', {
    id: contactValue.id,
    customAttributes: {
      ...existing,
      crm_note_target: { module: record.module, record_id: record.id },
    },
  });
};

const isBusy = ref(false);

const onClick = async () => {
  // 1) Destino recordado → abrir directo.
  if (target.value?.module && target.value?.record_id) {
    openUrl(recordUrl(target.value));
    return;
  }
  // 2) Buscar y elegir automáticamente el más reciente no-caído.
  if (isBusy.value) return;
  isBusy.value = true;
  try {
    const records = await store.dispatch('zohoCrmIntegration/searchRecords', {
      phone: phone.value,
      email: email.value,
    });
    const best = records.find(r => !r.fallen) || records[0];
    if (!best) {
      useAlert(t('CRM_NOTE_SYNC.NOT_FOUND'));
      return;
    }
    // eslint-disable-next-line no-console
    console.log(
      '[open-in-crm] auto-selected',
      best.module,
      best.id,
      'state=',
      best.state
    );
    rememberTarget(best);
    openUrl(recordUrl({ module: best.module, record_id: best.id }));
  } catch (e) {
    const msg = e?.response?.data?.error || e?.message || 'error';
    // eslint-disable-next-line no-console
    console.error('[open-in-crm] search failed:', msg);
    useAlert(t('CRM_NOTE_SYNC.SEARCH_ERROR', { error: msg }));
  } finally {
    isBusy.value = false;
  }
};
</script>

<template>
  <button
    v-if="isVisible"
    type="button"
    class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-background px-3 py-1.5 text-xs font-medium text-n-slate-12 hover:bg-n-alpha-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    :disabled="isBusy"
    :title="t('CRM_NOTE_SYNC.OPEN_IN_CRM_TOOLTIP')"
    @click="onClick"
  >
    <span
      class="size-3.5"
      :class="
        isBusy ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-external-link'
      "
    />
    <span class="hidden md:inline">{{ t('CRM_NOTE_SYNC.OPEN_IN_CRM') }}</span>
  </button>
</template>
